<div class="sof-queue">
  <!-- Loading -->
  <div class="sof-queue-loading" *ngIf="state.ordersLoading | async">
    <ion-spinner name="dots"></ion-spinner>
    <span>Loading orders…</span>
  </div>

  <ng-container *ngIf="!(state.ordersLoading | async)">
    <!-- Selected location has no corresponding storage facility -->
    <div class="sof-queue-empty" *ngIf="!state.storageFacility">
      <ion-icon name="alert-circle-outline" color="warning"></ion-icon>
      <div class="sof-queue-empty-title">No storage facility here</div>
      <div class="sof-queue-empty-text">
        {{ state.locationLabel }} has no SOF storage facility. Pick another
        location from the menu.
      </div>
    </div>

    <!-- Filter: SOF number, job number, or PO number. Client-side over the
         already-loaded queue; PO numbers come from the cards' own meta. -->
    <div class="sof-queue-search" *ngIf="state.storageFacility && (state.orders | async)?.length">
      <ion-icon name="search-outline" class="sof-queue-search-icon"></ion-icon>
      <ion-input
        class="sof-queue-search-input"
        placeholder="Filter by SOF, job, or PO number"
        [value]="searchTerm"
        (ionInput)="onSearchChange($event)"
        [attr.aria-label]="'Filter orders by SOF, job, or PO number'"
      ></ion-input>
      <button
        type="button"
        class="sof-queue-search-clear"
        *ngIf="searchTerm"
        (click)="clearSearch()"
        aria-label="Clear the filter"
      >
        <ion-icon name="close-circle"></ion-icon>
      </button>
    </div>

    <!-- Status chips: every state on by default, tap to exclude. On-hold SOFs
         carry 'On Hold' as their receivingStatus and match no chip, so they
         stay visible whatever is switched off (see status-filter.ts). -->
    <div class="sof-status-filter-wrap" *ngIf="state.storageFacility && (state.orders | async)?.length">
      <div class="sof-status-filter">
        <button
          type="button"
          class="sof-status-toggle"
          [class.sof-status-toggle-off]="!isStatusActive(key)"
          [attr.aria-pressed]="isStatusActive(key)"
          *ngFor="let key of statusKeys"
          (click)="toggleStatus(key)"
        >
          {{ statusLabels[key] }}
          <span class="sof-status-toggle-count">
            {{ (statusCounts$ | async)?.[key] ?? 0 }}
          </span>
        </button>
      </div>
      <!-- Outside the scrolling row on purpose: inside it, this scrolled off
           the right edge and was unreachable exactly when it was needed. -->
      <button
        type="button"
        class="sof-queue-search-reset sof-status-reset"
        *ngIf="statusFiltered"
        (click)="resetStatuses()"
      >
        Show all
      </button>
    </div>

    <!-- Empty queue -->
    <div
      class="sof-queue-empty"
      *ngIf="state.storageFacility && (state.orders | async)?.length === 0"
    >
      <ion-icon name="checkmark-circle-outline" color="success"></ion-icon>
      <div class="sof-queue-empty-title">
        {{ readOnly ? 'No order history' : 'No open orders' }}
      </div>
      <div class="sof-queue-empty-text">
        {{
          readOnly
            ? 'No completed orders at ' + state.locationLabel + ' yet.'
            : 'Nothing to pick at ' + state.locationLabel + ' right now.'
        }}
      </div>
    </div>

    <!-- Filters matched nothing (the queue itself is not empty). Either filter
         can be the cause, so the message names whichever is active. -->
    <div
      class="sof-queue-empty"
      *ngIf="(searchTerm || statusFiltered) && (visibleOrders$ | async)?.length === 0 && (state.orders | async)?.length"
    >
      <ion-icon name="search-outline" color="medium"></ion-icon>
      <div class="sof-queue-empty-title">
        {{ searchTerm ? 'No match for "' + searchTerm + '"' : 'Nothing in those statuses' }}
      </div>
      <div class="sof-queue-empty-text">
        {{
          searchTerm && statusFiltered
            ? 'Nothing matches that search within the statuses you have on.'
            : searchTerm
              ? 'Nothing here matches that SOF, job, or PO number.'
              : 'Every order here is in a status you have switched off.'
        }}
        <button type="button" class="sof-queue-search-reset" (click)="clearAllFilters()">
          Clear filters
        </button>
      </div>
    </div>

    <!-- SOF cards, soonest delivery date first (sortOrdersSoonestFirst) -->
    <button
      type="button"
      class="sof-card"
      *ngFor="let order of visibleOrders$ | async"
      [class.sof-card-selected]="
        (state.selectedOrder | async)?.sof === order.sof
      "
      (click)="selectSof(order)"
    >
      <div class="sof-card-top">
        <div class="sof-card-main">
          <div class="sof-card-title-row">
            <span class="sof-card-number">SOF {{ order.sof }}</span>
            <span
              class="sof-pill sof-pill-partial"
              *ngIf="(state.metaBySof | async)?.[order.sof]?.hasPartial"
              >Partial PO</span
            >
            <span class="sof-pill sof-pill-hold" *ngIf="onHold(order)"
              >ON HOLD</span
            >
          </div>
          <div class="sof-card-order-to">{{ order.orderTo }}</div>
          <!-- Date required sits under the job line, not on the title row —
               it qualifies the job, and the title row is the SOF number and
               its state pills. -->
          <div class="sof-card-date" *ngIf="dateRequiredFor(order) as required">
            <span class="sof-card-date-label">Date Required</span>
            {{ required | date : 'MM/dd/yyyy' }}
          </div>
          <div class="sof-card-meta">{{ order.contactName }}</div>
        </div>
        <span
          class="sof-status-chip"
          [ngClass]="'sof-status-chip-' + chipFor(order).color"
        >
          <span class="sof-status-dot"></span>
          {{ chipFor(order).label }}
        </span>
      </div>

      <!-- Aggregate meta strip: PO count · pulled/total · locations -->
      <div class="sof-card-metastrip" *ngIf="(state.metaBySof | async)?.[order.sof] as meta">
        <span class="sof-meta-item">
          <ion-icon name="cube-outline"></ion-icon>
          {{ meta.poCount }} PO{{ meta.poCount === 1 ? '' : 's' }}
        </span>
        <span class="sof-meta-item">
          <ion-icon name="checkmark-circle-outline"></ion-icon>
          {{ meta.pulledCount }}/{{ meta.totalCount }} pulled
        </span>
        <span class="sof-meta-item">
          <ion-icon name="navigate-outline"></ion-icon>
          {{ meta.locationCount }} location{{ meta.locationCount === 1 ? '' : 's' }}
        </span>
        <ion-icon class="sof-meta-chevron" name="chevron-forward-outline"></ion-icon>
      </div>
    </button>
  </ng-container>
</div>
